import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { webhookListenUrl } from '@/lib/api';
import { useT } from '@/lib/i18n';

// A decisão da Meta sobre um template chega pelo webhook dela; o servidor grava o
// status e emite template.* — que já passa pelo canal ao vivo do projeto (o mesmo
// do relay de webhooks). A tela só escuta: sem consulta periódica, a lista se
// atualiza e um aviso diz o que a Meta decidiu. EventSource reconecta sozinho.

interface Envelope {
  event_type?: string;
  payload?: { name?: string; language?: string; rejected_reason?: string; category?: string; quality?: string };
}

export function useTemplateLiveUpdates(enabled = true) {
  const qc = useQueryClient();
  const tr = useT();
  // A tradução fica numa ref: com ela nas dependências, uma troca de idioma (ou um
  // useT que muda de identidade) reabriria a conexão.
  const tRef = useRef(tr);
  tRef.current = tr;
  useEffect(() => {
    const t: typeof tr = (...a) => tRef.current(...a);
    if (!enabled || typeof EventSource === 'undefined') return;
    const es = new EventSource(webhookListenUrl());
    es.addEventListener('webhook.event', (e) => {
      let env: Envelope;
      try { env = JSON.parse((e as MessageEvent).data) as Envelope; } catch { return; }
      const type = env.event_type ?? '';
      if (!type.startsWith('template.')) return;
      // Lista, cartões (alternativa e código) e desempenho: tudo sob ['templates'].
      void qc.invalidateQueries({ queryKey: ['templates'] });
      const p = env.payload ?? {};
      const name = p.name ?? '';
      switch (type) {
        case 'template.approved':
          toast.success(t('templates.live.approved', { name }));
          break;
        case 'template.rejected':
          toast.error(t('templates.live.rejected', { name, reason: p.rejected_reason || t('templates.live.noReason') }), { duration: 15000 });
          break;
        case 'template.paused':
          toast.warning(t('templates.live.paused', { name }), { duration: 15000 });
          break;
        case 'template.categorized':
          toast.warning(t('templates.live.categorized', { name, category: t(`templates.category.${(p.category ?? '').toUpperCase()}`) }), { duration: 15000 });
          break;
        case 'template.quality_changed':
          toast.info(t('templates.live.quality', { name, quality: p.quality ?? '' }));
          break;
      }
    });
    return () => es.close();
  }, [enabled, qc]);
}
